import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Zap, Plus, Trash2 } from 'lucide-react';

// Available capabilities in the system
const AVAILABLE_CAPABILITIES = [
  { id: 'Net.HTTP', name: 'Net.HTTP', description: 'HTTP network requests' },
  { id: 'Storage.Local', name: 'Storage.Local', description: 'Browser local storage' },
  { id: 'Storage.KV', name: 'Storage.KV', description: 'Key-value store' },
  { id: 'Storage.DO', name: 'Storage.DO', description: 'Durable Objects' },
  { id: 'Storage.Supabase', name: 'Storage.Supabase', description: 'Supabase database' },
  { id: 'Auth.OAuth.Google', name: 'Auth.OAuth.Google', description: 'Google OAuth' },
  { id: 'Crypto.VerifyJWT', name: 'Crypto.VerifyJWT', description: 'JWT verification' },
  { id: 'Clock.Now', name: 'Clock.Now', description: 'Current time access' },
  { id: 'Queue.Offline', name: 'Queue.Offline', description: 'Offline queue' },
  { id: 'Log.Trace', name: 'Log.Trace', description: 'Trace logging' },
  { id: 'Compile.Codegen', name: 'Compile.Codegen', description: 'Code generation' },
];

/**
 * Effect Editor - Create/Edit Effects with Capabilities
 * 
 * Effects are the bridge between state machine transitions and
 * capability-governed side effects.
 */
const EffectEditor = ({ 
  effect = null, // null for new effect
  existingEffects = [],
  onSave, 
  onCancel,
  inline = true
}) => {
  const [name, setName] = useState(effect?.name || '');
  const [inputType, setInputType] = useState(effect?.input_type || 'void');
  const [outputType, setOutputType] = useState(effect?.output_type || 'void');
  const [failureType, setFailureType] = useState(effect?.failure_type || 'Error');
  const [selectedCapabilities, setSelectedCapabilities] = useState(
    effect?.required_capabilities?.map(c => c.name) || []
  );
  const [errors, setErrors] = useState([]);

  const validate = () => {
    const errs = [];
    if (!name.trim()) {
      errs.push('Effect name is required');
    }
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      errs.push('Effect name must start with letter and contain only alphanumeric characters');
    }
    if (!effect && existingEffects.includes(name)) {
      errs.push('Effect with this name already exists');
    }
    setErrors(errs);
    return errs.length === 0;
  };

  const handleCapabilityToggle = (capId) => {
    setSelectedCapabilities(prev => 
      prev.includes(capId) 
        ? prev.filter(c => c !== capId)
        : [...prev, capId]
    );
  };

  const handleSave = () => {
    if (!validate()) return;
    
    onSave({
      name,
      input_type: inputType,
      output_type: outputType,
      failure_type: failureType,
      required_capabilities: selectedCapabilities.map(cap => ({ name: cap }))
    });
  };

  if (inline) {
    return (
      <div 
        className="bg-[#1a1f3a] border-2 border-purple-500 rounded-lg p-3 shadow-xl z-50 w-80"
        data-testid="effect-editor-inline"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-bold">{effect ? 'Edit Effect' : 'Create Effect'}</h3>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Effect Name */}
          <div>
            <Label className="text-xs">Effect Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="FetchData"
              className="mt-1 h-8 text-sm"
              autoFocus
              data-testid="effect-name-input"
            />
          </div>

          {/* Type Signature */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-gray-500">Input</Label>
              <Input
                value={inputType}
                onChange={(e) => setInputType(e.target.value)}
                placeholder="void"
                className="mt-1 h-7 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Output</Label>
              <Input
                value={outputType}
                onChange={(e) => setOutputType(e.target.value)}
                placeholder="void"
                className="mt-1 h-7 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Failure</Label>
              <Input
                value={failureType}
                onChange={(e) => setFailureType(e.target.value)}
                placeholder="Error"
                className="mt-1 h-7 text-xs font-mono"
              />
            </div>
          </div>

          {/* Required Capabilities */}
          <div>
            <Label className="text-xs flex items-center">
              Required Capabilities
              <Badge variant="outline" className="ml-2 text-[9px] h-4">
                {selectedCapabilities.length} selected
              </Badge>
            </Label>
            <ScrollArea className="h-32 mt-2 border border-white/10 rounded p-2">
              <div className="space-y-1">
                {AVAILABLE_CAPABILITIES.map((cap) => (
                  <div 
                    key={cap.id}
                    className="flex items-center space-x-2 py-1 hover:bg-white/5 rounded px-1"
                  >
                    <Checkbox
                      id={`cap-${cap.id}`}
                      checked={selectedCapabilities.includes(cap.id)}
                      onCheckedChange={() => handleCapabilityToggle(cap.id)}
                    />
                    <label 
                      htmlFor={`cap-${cap.id}`}
                      className="flex-1 text-xs cursor-pointer"
                    >
                      <span className="font-mono">{cap.name}</span>
                      <span className="text-gray-500 ml-2 text-[10px]">{cap.description}</span>
                    </label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="text-xs text-red-400 space-y-1">
              {errors.map((err, idx) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-2 pt-2">
            <Button 
              onClick={handleSave} 
              size="sm" 
              className="flex-1 h-8 text-xs bg-purple-600 hover:bg-purple-700"
              data-testid="effect-save-button"
            >
              <Zap className="w-3 h-3 mr-1" />
              {effect ? 'Update Effect' : 'Create Effect'}
            </Button>
            <Button 
              onClick={onCancel} 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null; // Full-screen version not needed for desktop-first
};

export default EffectEditor;
